import { marked } from 'marked';
import DOMPurify from 'dompurify';

const markdownPatterns = [
  /^#{1,6}\s+/m,
  /^\s*[-*+]\s+/m,
  /^\s*\d+\.\s+/m,
  /\*\*.*\*\*/,
  /\*[^*]+\*/,
  /\[.+\]\(.+\)/,
  /!\[.+\]\(.+\)/
];

const isMarkdown = (text) => markdownPatterns.some(p => p.test(text));

const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  return match ? match[1] : null;
};

const extractCanvaId = (url) => {
  if (!url) return null;
  const match = url.match(/canva\.com\/design\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

const extractIframeSrc = (html) => {
  if (!html) return null;
  const match = html.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : null;
};

const isOnlyUrl = (text, pattern) => {
  const trimmed = text.trim();
  const match = trimmed.match(pattern);
  return match && match[1] === trimmed;
};

const extractUrlFromText = (text, regex) => {
  const match = text.match(regex);
  return match ? match[1] : null;
};

const youtubeUrlPattern = /(https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s]+)/;
const canvaUrlPattern = /(https?:\/\/(?:www\.)?canva\.com\/design\/[^\s]+)/;

const sanitizeInlineHtml = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['br', 'a', 'strong', 'em', 'b', 'i', 'u', 's', 'span', 'sub', 'sup'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'title']
  });
};

const parseNode = (node) => {
  const blocks = [];

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent.trim();
    if (text) blocks.push({ type: 'paragraph', text: sanitizeInlineHtml(text) });
    return blocks;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return blocks;

  const tag = node.tagName.toLowerCase();

  if (/^h[1-6]$/.test(tag)) {
    blocks.push({ type: 'heading', level: tag, text: sanitizeInlineHtml(node.innerHTML) });
    return blocks;
  }

  if (tag === 'img') {
    let url = node.getAttribute('src') || '';
    if (url.startsWith('data:') || url.startsWith('file:')) url = '';
    blocks.push({ type: 'image', url });
    return blocks;
  }

  if (tag === 'ul' || tag === 'ol') {
    const items = Array.from(node.querySelectorAll(':scope > li')).map(li =>
      sanitizeInlineHtml(li.innerHTML)
    );
    if (items.length > 0) {
      blocks.push({ type: 'list', ordered: tag === 'ol', items });
    }
    return blocks;
  }

  if (tag === 'p') {
    const text = node.textContent.trim();
    const html = sanitizeInlineHtml(node.innerHTML);

    const youtubeUrl = extractUrlFromText(text, youtubeUrlPattern);
    if (youtubeUrl && extractYouTubeId(youtubeUrl) && isOnlyUrl(text, youtubeUrlPattern)) {
      blocks.push({ type: 'youtube', url: youtubeUrl });
      return blocks;
    }

    const canvaUrl = extractUrlFromText(text, canvaUrlPattern);
    if (canvaUrl && extractCanvaId(canvaUrl) && isOnlyUrl(text, canvaUrlPattern)) {
      blocks.push({ type: 'canva', url: canvaUrl });
      return blocks;
    }

    if (html) blocks.push({ type: 'paragraph', text: html });
    return blocks;
  }

  if (tag === 'iframe') {
    const src = node.getAttribute('src') || extractIframeSrc(node.outerHTML);
    if (extractYouTubeId(src)) {
      blocks.push({ type: 'youtube', url: src });
    } else if (extractCanvaId(src)) {
      blocks.push({ type: 'canva', url: src });
    } else {
      blocks.push({ type: 'iframe', url: src });
    }
    return blocks;
  }

  if (tag === 'figure') {
    const img = node.querySelector('img');
    if (img) {
      let url = img.getAttribute('src') || '';
      if (url.startsWith('data:') || url.startsWith('file:')) url = '';
      blocks.push({ type: 'image', url });
    }
    return blocks;
  }

  if (tag === 'div' || tag === 'section' || tag === 'article' || tag === 'main' || tag === 'blockquote') {
    Array.from(node.childNodes).forEach(child => {
      blocks.push(...parseNode(child));
    });
    return blocks;
  }

  if (tag === 'br') return blocks;

  const fallback = sanitizeInlineHtml(node.innerHTML).trim();
  if (fallback) blocks.push({ type: 'paragraph', text: fallback });
  return blocks;
};

export const parsePastedContent = (rawInput) => {
  if (!rawInput || !rawInput.trim()) return [];

  let html = rawInput.trim();

  if (isMarkdown(html)) {
    html = marked(html, { breaks: true, gfm: true });
  }

  const allowedTags = [
    'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'img', 'iframe', 'a', 'strong', 'em',
    'b', 'i', 'u', 's', 'span', 'div', 'section', 'article', 'main', 'figure', 'figcaption', 'blockquote', 'sub', 'sup'
  ];

  const allowedAttrs = ['src', 'href', 'alt', 'title', 'class', 'style', 'target', 'rel'];

  html = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttrs
  });

  const container = document.createElement('div');
  container.innerHTML = html;

  const blocks = [];
  Array.from(container.childNodes).forEach(node => {
    blocks.push(...parseNode(node));
  });

  return blocks;
};
